/**
 * Background community post uploads (Facebook-style).
 * Survives CreatePostForm / CommunityFeed unmount so users can navigate away.
 *
 * Flow: getPresignedUrl from API → PUT directly to Hetzner (or local-upload in
 * pure-dev without S3) → createPost with object key.
 */
import api from '../api/client';

/** @typedef {'queued'|'preparing'|'uploading'|'posting'|'done'|'error'} UploadStatus */

/**
 * @typedef {object} CommunityUploadJob
 * @property {string} tempId
 * @property {string} content
 * @property {File|null} imageFile
 * @property {File|null} videoFile
 * @property {Promise<File|null>|null} [videoPreparePromise] optional bake/compress before upload
 * @property {number|null} videoDurationSeconds
 * @property {string|null} imagePreviewUrl
 * @property {string|null} videoPreviewUrl
 * @property {object|null} currentUser
 * @property {UploadStatus} status
 * @property {number} progress 0..100
 * @property {string} [error]
 * @property {object} [post] final server post
 * @property {number} createdAt
 */

const listeners = new Set();
/** @type {CommunityUploadJob[]} */
let jobs = [];
let processing = false;

function emit(event) {
  listeners.forEach((fn) => {
    try {
      fn(event, getSnapshot());
    } catch {
      /* ignore listener errors */
    }
  });
}

function getSnapshot() {
  return jobs.map((j) => ({ ...j }));
}

function updateJob(tempId, patch) {
  jobs = jobs.map((j) => (j.tempId === tempId ? { ...j, ...patch } : j));
  const job = jobs.find((j) => j.tempId === tempId);
  emit({ type: 'update', job });
  return job;
}

function revokePreview(job) {
  if (job.imagePreviewUrl?.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(job.imagePreviewUrl);
    } catch {
      /* ignore */
    }
  }
  if (job.videoPreviewUrl?.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(job.videoPreviewUrl);
    } catch {
      /* ignore */
    }
  }
}

/**
 * PUT a file to a presigned URL (Hetzner) with upload progress.
 * @param {string} uploadUrl
 * @param {File|Blob} file
 * @param {Record<string, string>} headers
 * @param {(pct: number) => void} [onProgress]
 */
function putWithProgress(uploadUrl, file, headers, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    Object.entries(headers || {}).forEach(([k, v]) => {
      if (v != null) xhr.setRequestHeader(k, v);
    });
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !onProgress) return;
      onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status}). Please try again.`));
    };
    xhr.onerror = () => reject(new Error('Upload failed. Please check your connection and try again.'));
    xhr.onabort = () => reject(new Error('Upload cancelled.'));
    xhr.send(file);
  });
}

async function uploadMediaFile(file, { onProgress } = {}) {
  const slot = await api.community.getPostMediaUploadUrl({
    contentType: file.type || 'application/octet-stream',
  });
  const { uploadUrl, objectKey, contentType, cacheControl, maxBytes } = slot.data || {};
  if (!uploadUrl || !objectKey) throw new Error('Could not start upload.');
  if (maxBytes && file.size > maxBytes) throw new Error('File is too large.');

  const putHeaders = { 'Content-Type': contentType || file.type || 'application/octet-stream' };
  if (cacheControl) putHeaders['Cache-Control'] = cacheControl;

  await putWithProgress(uploadUrl, file, putHeaders, onProgress);
  return { objectKey, contentType: contentType || file.type };
}

async function processJob(job) {
  const hasMedia = Boolean(job.imageFile || job.videoFile || job.videoPreparePromise);
  updateJob(job.tempId, {
    status: job.videoPreparePromise ? 'preparing' : 'uploading',
    progress: hasMedia ? 0 : 100,
    error: undefined,
  });

  let image_key;
  let image_content_type;
  let video_key;
  let video_content_type;
  let video_duration_seconds;

  try {
    let videoFile = job.videoFile;
    if (job.videoPreparePromise) {
      try {
        const prepared = await job.videoPreparePromise;
        if (prepared instanceof File || (prepared && prepared.size > 0)) {
          videoFile = prepared;
          updateJob(job.tempId, { videoFile });
        }
      } catch (err) {
        // Fall back to the original clip when bake/compress fails.
        if (!videoFile) throw err;
      }
    }

    if (job.imageFile) {
      updateJob(job.tempId, { status: 'uploading', progress: 0 });
      const uploaded = await uploadMediaFile(job.imageFile, {
        onProgress: (pct) => updateJob(job.tempId, { progress: pct, status: 'uploading' }),
      });
      image_key = uploaded.objectKey;
      image_content_type = uploaded.contentType;
    } else if (videoFile) {
      updateJob(job.tempId, { status: 'uploading', progress: 0 });
      const uploaded = await uploadMediaFile(videoFile, {
        onProgress: (pct) => updateJob(job.tempId, { progress: pct, status: 'uploading' }),
      });
      video_key = uploaded.objectKey;
      video_content_type = uploaded.contentType;
      video_duration_seconds = job.videoDurationSeconds;
    }

    updateJob(job.tempId, { status: 'posting', progress: 100 });
    const res = await api.community.createPost({
      content: job.content,
      ...(image_key ? { image_key, image_content_type } : {}),
      ...(video_key ? { video_key, video_content_type, video_duration_seconds } : {}),
    });
    const post = res.data?.post;
    const done = updateJob(job.tempId, { status: 'done', progress: 100, post });
    emit({ type: 'success', job: done, post });
    revokePreview(done);
    // Drop finished jobs after a short window so the status bar can flash success
    window.setTimeout(() => {
      jobs = jobs.filter((j) => j.tempId !== job.tempId);
      emit({ type: 'removed', tempId: job.tempId });
    }, 4000);
  } catch (err) {
    const message = err?.response?.data?.error || err?.message || 'Upload failed.';
    const failed = updateJob(job.tempId, { status: 'error', error: message });
    emit({ type: 'error', job: failed, error: message });
  }
}

async function pump() {
  if (processing) return;
  processing = true;
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const next = jobs.find((j) => j.status === 'queued');
      if (!next) break;
      await processJob(next);
    }
  } finally {
    processing = false;
  }
}

/**
 * Enqueue a community post for background upload.
 * @param {object} input
 * @returns {CommunityUploadJob}
 */
export function enqueueCommunityPost(input) {
  const tempId = input.tempId || `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  /** @type {CommunityUploadJob} */
  const job = {
    tempId,
    content: String(input.content || '').trim(),
    imageFile: input.imageFile || null,
    videoFile: input.videoFile || null,
    videoPreparePromise: input.videoPreparePromise || null,
    videoDurationSeconds: input.videoDurationSeconds ?? null,
    imagePreviewUrl: input.imagePreviewUrl || null,
    videoPreviewUrl: input.videoPreviewUrl || null,
    currentUser: input.currentUser || null,
    status: 'queued',
    progress: 0,
    createdAt: Date.now(),
  };
  jobs = [
    job,
    ...jobs.filter((j) =>
      ['error', 'preparing', 'uploading', 'posting', 'queued'].includes(j.status)
    ),
  ];
  emit({ type: 'enqueued', job });
  pump();
  return job;
}

export function retryCommunityUpload(tempId) {
  const job = jobs.find((j) => j.tempId === tempId);
  if (!job || job.status !== 'error') return;
  updateJob(tempId, { status: 'queued', progress: 0, error: undefined });
  pump();
}

export function dismissCommunityUpload(tempId) {
  const job = jobs.find((j) => j.tempId === tempId);
  if (job) revokePreview(job);
  jobs = jobs.filter((j) => j.tempId !== tempId);
  emit({ type: 'removed', tempId });
}

export function subscribeCommunityUploads(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getCommunityUploadJobs() {
  return getSnapshot();
}

/** @returns {boolean} true when any upload is in flight */
export function hasActiveCommunityUploads() {
  return jobs.some((j) =>
    ['queued', 'preparing', 'uploading', 'posting'].includes(j.status)
  );
}

/** Test helper */
export function __resetCommunityUploadQueueForTests() {
  jobs = [];
  processing = false;
  listeners.clear();
}

export function buildOptimisticCommunityPost(job) {
  const u = job.currentUser || {};
  const name =
    u.name ||
    [u.first_name, u.last_name].filter(Boolean).join(' ') ||
    u.email ||
    'You';
  const hasMedia = Boolean(job.imageFile || job.videoFile || job.videoPreparePromise);
  return {
    id: job.tempId,
    content: job.content,
    image_url: job.imagePreviewUrl || null,
    video_url: job.videoPreviewUrl || null,
    image_key: job.imageFile ? 'pending' : null,
    video_key: job.videoFile || job.videoPreparePromise ? 'pending' : null,
    media_moderation_status: hasMedia ? 'pending_review' : 'approved',
    media_moderation_notes: null,
    like_count: 0,
    liked_by_me: false,
    created_at: new Date(job.createdAt || Date.now()).toISOString(),
    user: {
      id: u.id,
      name,
      avatar_url: u.avatar_url || null,
    },
    user_id: u.id,
    _upload: {
      tempId: job.tempId,
      status: job.status,
      progress: job.progress || 0,
      error: job.error || null,
    },
  };
}
